<template>
  <div class="d-flex align-center" style="width:100%">
    <v-select
        :items="presets"
        v-model="preset"
        :label="presetLabel"
        dense outlined hide-details class="mr-2"
        @change="onPresetChange"
    />

    <v-menu
        v-model="menu"
        transition="scale-transition"
        offset-y
        :close-on-content-click="false"
        eager
    >
      <template #activator="{ on, attrs }">
        <v-text-field
            v-bind="attrs" v-on="on"
            dense outlined hide-details readonly
            :label="rangeLabel"
            :value="formatRangeLabel(internal.from, internal.to)"
            prepend-inner-icon="mdi-calendar-range"
            clearable
            @click:clear="clearRange"
        />
      </template>

      <v-date-picker v-model="rangeArr" range scrollable @change="applyCustomRange"/>
    </v-menu>
  </div>
</template>

<script>
export default {
  name: 'DateRangeQuick',
  props: {
    /* v-model: { from: ISO|string|Date|null, to: ISO|string|Date|null } */
    value: { type: Object, default: function(){ return { from: null, to: null }; } },
    label: { type: String, default: '' },
    presetLabel: { type: String, default: 'Periodo' },
    defaultPreset: { type: String, default: null},
    normalizeDayBounds: { type: Boolean, default: true }
  },
  data: function(){
    return {
      menu: false,
      preset: this.defaultPreset,
      internal: { from: null, to: null }, // ISO
      rangeArr: [], // ['YYYY-MM-DD','YYYY-MM-DD'] per v-date-picker
      presets: [
        { text: 'Ultima settimana', value: 'last_week' },
        { text: 'Ultimo mese',     value: 'last_month' },
        { text: 'Ultimi 30 giorni', value: 'last_30' },
        { text: 'Personalizzato',  value: 'custom' },
        { text: '',  value: null }
      ]
    };
  },
  computed: {
    rangeLabel: function () {
      return this.label ? (this.label + ' (range)') : 'Range';
    }
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler: function (v) {
        let fromIso = v && v.from ? this.toISO(v.from, true) : null;
        let toIso   = v && v.to   ? this.toISO(v.to,   false) : null;

        this.internal = { from: fromIso, to: toIso };
        this.rangeArr = this.isoPairToYmdArr(this.internal.from, this.internal.to);

        if (!fromIso && !toIso) {
          this.preset = null;
          this.menu = false;
        }
      }
    }
  },
  mounted: function(){
    if (!this.internal.from && !this.internal.to && this.preset !== 'custom') {
      this.applyPreset(this.preset, false);
    }
  },
  methods: {
    // ---- util date ----
    toISO: function (d, startOfDay) {
      if (!d) return null;
      var dt = d instanceof Date ? new Date(d.getTime()) : new Date(d);
      if (isNaN(dt.getTime())) return null;
      if (this.normalizeDayBounds) {
        if (typeof startOfDay === 'boolean') {
          if (startOfDay) dt.setHours(0,0,0,0);
          else dt.setHours(23,59,59,999);
        }
      }
      return dt.toISOString();
    },
    toYmd: function (d) {
      if (!d) return null;
      var dt = d instanceof Date ? d : new Date(d);
      if (isNaN(dt.getTime())) return null;
      var m = (dt.getMonth()+1).toString().padStart(2,'0');
      var day = dt.getDate().toString().padStart(2,'0');
      return dt.getFullYear() + '-' + m + '-' + day;
    },
    ymdToISO: function (ymd, startOfDay) {
      if (!ymd) return null;
      var parts = ymd.split('-');
      var dt = new Date(parseInt(parts[0],10), parseInt(parts[1],10)-1, parseInt(parts[2],10));
      return this.toISO(dt, startOfDay);
    },
    isoPairToYmdArr: function (fromIso, toIso) {
      var arr = [];
      if (fromIso) arr.push(this.toYmd(fromIso));
      if (toIso)   arr.push(this.toYmd(toIso));
      return arr;
    },
    formatRangeLabel: function (from, to) {
      function fmt(iso) {
        if (!iso) return '—';
        var d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        return d.toLocaleDateString('it-IT');
      }
      return fmt(from) + ' → ' + fmt(to);
    },

    // ---- preset e interazioni ----
    onPresetChange: function (val) {
      if (val === 'custom') {
        // apri il calendario subito
        this.menu = true;
        return;
      }
      this.applyPreset(val, false);
    },

    applyPreset: function (val, silent) {
      var now = new Date();
      var fromIso = null, toIso = null;

      if (val === 'last_week') {
        var d7 = new Date(now); d7.setDate(d7.getDate() - 7);
        fromIso = this.toISO(d7, true);
        toIso   = this.toISO(now, false);
      }
      if (val === 'last_30') {                                  // <— NEW
        var d30 = new Date(now); d30.setDate(d30.getDate() - 30);
        fromIso = this.toISO(d30, true);
        toIso   = this.toISO(now, false);
      }
      if (val === 'last_month') {
        var firstPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        var lastPrev  = new Date(now.getFullYear(), now.getMonth(), 0);
        fromIso = this.toISO(firstPrev, true);
        toIso   = this.toISO(lastPrev,  false);
      }

      this.internal = { from: fromIso, to: toIso };
      this.rangeArr = this.isoPairToYmdArr(fromIso, toIso);

      if (!silent) this.emitChange();
      this.menu = false;
    },

    applyCustomRange: function () {
      if (Array.isArray(this.rangeArr) && this.rangeArr.length === 2) {
        var fromIso = this.ymdToISO(this.rangeArr[0], true);
        var toIso   = this.ymdToISO(this.rangeArr[1], false);
        this.internal = { from: fromIso, to: toIso };
        this.preset = 'custom';
        this.menu = false;
        this.emitChange();
      }
    },

    clearRange: function () {
      this.internal = { from: null, to: null };
      this.rangeArr = [];
      this.preset = null;
      this.emitChange();
    },

    emitChange: function () {
      var payload = { from: this.internal.from, to: this.internal.to };
      this.$emit('input', payload);
      this.$emit('change', payload);
    }
  }
};
</script>
